import React, { useCallback, useEffect, useState } from 'react';
import { Button, Form, Input } from 'antd';
import { useTranslation } from 'react-i18next';
import { faTrashAlt } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Card } from '@cards/Card';
import useWizard from '@components/Wizard/useWizard';

const roleNameTest = /role-[0-9]{2}--name/;
const roleNumberTest = /role-[0-9]{2}/;

function CareTeamRoles() {
  const [roles, setRoles] = useState([]);
  const { t } = useTranslation();
  const { currentStepData, form, formData, setCurrentStepData } = useWizard();

  useEffect(() => {
    if (Array.isArray(formData.careTeamRoles) && formData.careTeamRoles.length > 0) {
      const roleKeys = formData.careTeamRoles.map((_, index) => {
        const roleNumber = `0${index + 1}`.slice(-2);
        return `role-${roleNumber}`;
      });
      setRoles(roleKeys);

      const roleFieldValues = formData.careTeamRoles.reduce((acc, role, index) => {
        const roleKey = roleKeys[index];
        return {
          ...acc,
          [`${roleKey}--name`]: role.name,
          [`${roleKey}--description`]: role.description || '',
        };
      }, {});

      form.setFieldsValue({
        careTeamRoles: formData.careTeamRoles,
        ...roleFieldValues,
      });

      setCurrentStepData({
        ...currentStepData,
        ...roleFieldValues,
      });
    } else {
      const existingRoles = Object.keys(currentStepData)
        .filter(key => key.match(roleNameTest))
        .map(role => role.match(roleNumberTest)[0]);

      if (existingRoles.length) {
        setRoles(existingRoles);
      } else {
        setRoles([]);
      }

      if (formData.careTeamRoles) {
        form.setFieldsValue({ careTeamRoles: formData.careTeamRoles });
      }
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const addRole = useCallback(() => {
    if (roles.length >= 99) return;
    const rolesValue = form.getFieldValue('careTeamRoles');
    const existingRoles = Array.isArray(rolesValue) ? rolesValue : [];

    form.setFieldsValue({
      careTeamRoles: [
        ...existingRoles,
        {
          name: '',
          description: '',
        },
      ],
    });

    setRoles([
      ...roles,
      `role-${(
        '0' +
        (roles[roles.length - 1] ? Number(roles[roles.length - 1].replace('role-', '')) + 1 : 1)
      ).slice(-2)}`,
    ]);
  }, [form, roles]);

  const deleteRole = useCallback(
    (role, index) => {
      const updatedStepData = Object.entries(currentStepData).reduce((updated, [key, data]) => {
        if ([`${role}--name`, `${role}--description`].includes(key)) return updated;
        return { ...updated, [key]: data };
      }, {});
      setCurrentStepData(updatedStepData);
      const existingRoles = form.getFieldValue('careTeamRoles');

      if (existingRoles?.length > 0) {
        form.setFieldsValue({
          careTeamRoles: [...existingRoles.slice(0, index), ...existingRoles.slice(index + 1)],
        });
      }
      setRoles([...roles.slice(0, index), ...roles.slice(index + 1)]);
    },
    [currentStepData, form, roles, setCurrentStepData],
  );

  return (
    <>
      <Card>
        <p className="info-text">{t('pathways:ProcedurePathways.wizard.careTeam.info')}</p>
      </Card>
      <Card.Full className="roles-header-card">
        <h3>{t('pathways:ProcedurePathways.wizard.careTeam.header')}</h3>
        <Button icon="plus" type="dashed" onClick={addRole}>
          {t('pathways:ProcedurePathways.wizard.careTeam.newRole')}
        </Button>
      </Card.Full>
      {form.getFieldDecorator('careTeamRoles')(<span />)}
      {roles.map((role, index) => (
        <Card.Full className="pathway-role-card" key={role}>
          <div className="role-content-input">
            <Form.Item label={t('pathways:ProcedurePathways.wizard.careTeam.title')}>
              {form.getFieldDecorator(`${role}--name`, {
                initialValue: currentStepData[`${role}--name`],
                rules: [
                  {
                    required: true,
                    message: t('pathways:ProcedurePathways.wizard.careTeam.titleError'),
                  },
                ],
              })(
                <Input
                  placeholder={t('pathways:ProcedurePathways.wizard.careTeam.titlePlaceholder')}
                />,
              )}
            </Form.Item>
            <Form.Item label={t('pathways:ProcedurePathways.wizard.careTeam.description')}>
              {form.getFieldDecorator(`${role}--description`, {
                initialValue: currentStepData[`${role}--description`] || '',
              })(
                <Input
                  placeholder={t(
                    'pathways:ProcedurePathways.wizard.careTeam.descriptionPlaceholder',
                  )}
                />,
              )}
            </Form.Item>
          </div>
          <div className="role-deleter">
            <Button type="link" onClick={() => deleteRole(role, index)}>
              <FontAwesomeIcon icon={faTrashAlt} />
            </Button>
          </div>
        </Card.Full>
      ))}
    </>
  );
}

export default CareTeamRoles;
